import { FilterCondition } from "@irohana-dev/components";
import { FC, useMemo } from "react";
import { partnerSearchConfig } from "@/api/services/main/partner";
import { TSavedConditionShareScope } from "@/api/services/main/saved-condition/dtos/saved-condition.dto";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { useFilterConditionPanel } from "@/components/commons/filter-condition/use-filter-condition-panel";
import { TFilterConditionPanelConfig } from "@/components/commons/filter-condition/types";
import { buildTalentFilterFields, fromFilterValue, toFilterValue } from "@/features/talent/components/talent-list/talent-filter-condition-adapter";
import {
  TTalentDisplayColumnSetting,
  TTalentFilterFormExtended,
  defaultTalentDisplayColumns,
  defaultTalentFilterValues,
  normalizeTalentDateFields,
  normalizeTalentMultiSelectFields,
  stripEmptyTalentFilterFields,
  TALENT_LOCKED_COLUMN_IDS,
} from "@/features/talent/components/talent-list/talent-filter-shared";
import { useTalentFilterMasterData } from "@/features/talent/components/talent-list/use-talent-filter-master-data";
import { TALENT_DISPLAY_COLUMNS_STORAGE_KEY } from "@/features/talent/constants/common";
import { TALENT_LOCATION_OPTIONS } from "@/types/enum";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";
import { FILTER_CONDITION_PANEL_WIDTH } from "@/components/commons/filter-condition/filter-condition-panel-width";

const panelConfig: TFilterConditionPanelConfig<TTalentFilterFormExtended, TTalentDisplayColumnSetting> = {
  pageType: "talent",
  defaultFilters: defaultTalentFilterValues,
  defaultColumns: defaultTalentDisplayColumns,
  columnsStorageKey: TALENT_DISPLAY_COLUMNS_STORAGE_KEY,
  lockedColumnIds: TALENT_LOCKED_COLUMN_IDS,
  normalizeFilters: (filters) => normalizeTalentMultiSelectFields(normalizeTalentDateFields(filters)),
  toFilterValue,
  fromFilterValue,
  stripEmptyFilters: stripEmptyTalentFilterFields,
  getDateRange: (filters) => ({ from: filters.registrationDateFrom, to: filters.registrationDateTo }),
};

type Props = {
  isOpen: boolean;
  mode?: "create" | "edit";
  onClose: () => void;
  onApplyFilters?: (filters: TTalentFilterFormExtended) => void;
  onApplyDisplaySettings?: (columns: TTalentDisplayColumnSetting[]) => void;
  initialFilters?: Partial<TTalentFilterFormExtended>;
  initialDisplayColumns?: TTalentDisplayColumnSetting[];
  appliedConditionId?: number | null;
  appliedConditionName?: string;
  appliedConditionShareScope?: TSavedConditionShareScope;
  appliedConditionIsDefault?: boolean;
  appliedConditionIncludeColumnSettings?: boolean;
  appliedConditionColumnSettings?: { order?: string[]; hidden?: string[] } | null;
  onConditionSaved?: (id: number, title: string) => void;
};

export const TalentFilterConditionPanel: FC<Props> = ({ mode = "create", ...rest }) => {
  const { truncatedOptions } = useTalentFilterMasterData();
  const panel = useFilterConditionPanel(panelConfig, { ...rest, mode });

  const locationOptions = useMemo(() => TALENT_LOCATION_OPTIONS.map((o) => ({ label: o.label, value: String(o.value) })), []);

  const fields = useMemo(
    () => buildTalentFilterFields(truncatedOptions, locationOptions, partnerSearchConfig),
    [truncatedOptions, locationOptions],
  );

  return (
    <>
      <FilterCondition
        width={FILTER_CONDITION_PANEL_WIDTH}
        shareColor={filterConditionShareColor}
        isOpen={rest.isOpen}
        mode={panel.displayMode}
        onClose={rest.onClose}
        fields={fields}
        value={panel.value}
        onChange={panel.setValue}
        columns={panel.columnOptions}
        columnOrder={panel.columnOrder}
        hiddenColumns={panel.hiddenColumns}
        lockedColumns={TALENT_LOCKED_COLUMN_IDS}
        onColumnSettingsChange={panel.handleColumnSettingsChange}
        conditionName={panel.conditionName}
        onConditionNameChange={panel.handleConditionNameChange}
        conditionNameError={panel.conditionNameError}
        appliedConditionId={rest.appliedConditionId ?? undefined}
        shareScope={panel.shareScope}
        onShareScopeChange={panel.setShareScope}
        isDefault={panel.isDefault}
        onIsDefaultChange={panel.setIsDefault}
        includeColumnSettings={panel.includeColumnSettings}
        onIncludeColumnSettingsChange={panel.setIncludeColumnSettings}
        hasSavedColumnSettings={panel.hasSavedColumnSettings}
        onSaveCondition={panel.handleSaveCondition}
        isSaving={panel.isSaving}
        onApply={panel.handleApply}
        onClear={() => panel.hasDataToClear && panel.setIsClearConfirmOpen(true)}
      />
      <ConfirmDeleteModal
        open={panel.isClearConfirmOpen}
        onClose={() => panel.setIsClearConfirmOpen(false)}
        onOk={panel.clearValue}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
    </>
  );
};
